import { getDatePickerLocale } from 'client/javascript/date_picker/localization';
import { setupDatePickerFormField } from 'client/javascript/date_picker_field';
import Form from 'client/javascript/lead_form';
import { setupMarketSelectField } from 'client/javascript/market_select_field';
import NumberStepper from 'client/javascript/number_stepper';
import { setupPhoneInput } from 'client/javascript/phone';
import { showElements, hideElements } from 'client/javascript/util/dom_utils';
import { submitLead } from 'client/javascript/submit_lead';
import { sha256 } from 'client/javascript/util/krux';
import { sendLeadFormHeapEvents } from 'client/javascript/form_analytics';
import { getGoogleAnalyticsData } from 'client/javascript/analytics';
import { logCriticalError } from 'client/javascript/util/logger';
import { trackError } from 'tracking/error/analytics';
import { TEAMS } from 'shared/rollbar/global_prefixes';
import { LEAD_FORM } from 'shared/rollbar/lead_form_prefixes';
import { renderSingleUniqueModule } from 'client/javascript/util/geolocation_based_module_rendering';

export const SELECTORS = {
  MODULE_SECTION: '.js-dynamic-lead-form',
  TIME_INPUT: '.dynamic-form_time-input'
};
export const LOGGING_CONFIG = {
  prefix: '[SharedDynamicLeadFormUI]',
  team: TEAMS.SHARED
};

const errorsContainer = {
  selector: '#dynamic-submit-lead-form-error-message',
  show() {
    return showElements(this.selector);
  },
  hide() {
    return hideElements(this.selector);
  }
};

const FIELDS_IDS = {
  LOCATION_SELECT: '#dynamic-form-location-select',
  DATE_PICKER: '#dynamic-form-date',
  PHONE_INPUT: '#dynamic-form-phone',
  TIME_PICKER: '#dynamic-form-time',
  NUMBER_STEPPER: '#dynamic-form-stepper-number-stepper'
};

const getDatePickerOptions = (locale) => ({
  locale,
  minDate: 'today',
  allowInput: false,
  nextArrow: '➝',
  prevArrow: '➝',
  dateFormat: 'm/d/y',
  disableMobile: true,
  customValidation: (value) => !!value
});

const getTimePickerOptions = (timeFieldNode, locale) => ({
  locale,
  allowInput: false,
  enableTime: true,
  noCalendar: true,
  dateFormat: 'h:i K',
  disableMobile: true,
  defaultDate: '12:00',
  onReady: () => {
    timeFieldNode.querySelector('input').dispatchEvent(new Event('change'));
  },
  customValidation: (value) => {
    // We should add class 'ray-text-field--has-value' manually because after the first call onChange event it doesn't add automatically
    if (value) {
      timeFieldNode.classList.add('ray-text-field--has-value');
    }
    return !!value;
  }
});

async function initializeFormFields(leadForm) {
  const locale = await getDatePickerLocale();
  const phoneInputNode = leadForm.querySelector(FIELDS_IDS.PHONE_INPUT);
  const timePickerNode = leadForm.querySelector(SELECTORS.TIME_INPUT);
  const fieldInitializers = {
    [FIELDS_IDS.NUMBER_STEPPER]: () => new NumberStepper(leadForm.querySelector(FIELDS_IDS.NUMBER_STEPPER)),
    [FIELDS_IDS.LOCATION_SELECT]: () => setupMarketSelectField(FIELDS_IDS.LOCATION_SELECT),
    [FIELDS_IDS.PHONE_INPUT]: () => setupPhoneInput(phoneInputNode),
    [FIELDS_IDS.DATE_PICKER]: () => setupDatePickerFormField(FIELDS_IDS.DATE_PICKER, getDatePickerOptions(locale)),
    [FIELDS_IDS.TIME_PICKER]: () =>
      setupDatePickerFormField(FIELDS_IDS.TIME_PICKER, getTimePickerOptions(timePickerNode, locale))
  };

  Object.keys(fieldInitializers).forEach((selector) => {
    if (leadForm.querySelector(selector)) {
      fieldInitializers[selector]();
    }
  });
}

export async function initUniqueLeadForm(formLabel) {
  await renderSingleUniqueModule({ selector: SELECTORS.MODULE_SECTION }, 'Dynamic LeadForm');

  setUpLeadForm({ formLabel });
}

export function setUpLeadForm(options) {
  try {
    const leadFormContainer = document.getElementById('dynamic-lead-form') || document.getElementById('contact');

    if (leadFormContainer) {
      // eslint-disable-next-line no-new
      new Form(leadFormContainer, formSubmitHandler, options);
      initializeFormFields(leadFormContainer);
    }
  } catch (error) {
    const message = `${LEAD_FORM}${LOGGING_CONFIG.prefix}[setUpLeadForm()]${options.formLabel}`;
    logCriticalError(`${message} failed`, {
      stack: error.stack,
      errorMessage: error.message,
      notificationHandle: message,
      team: LOGGING_CONFIG.team
    });
  }
}

async function formSubmitHandler(event, values) {
  if (event) {
    event.preventDefault();
  }

  errorsContainer.hide();

  try {
    const submitLeadData = await buildLeadData(values);
    await submitLead(submitLeadData, values.leadSourceDetail);
    await trackFormSubmission(values);
    showThankYou();
  } catch (error) {
    // Tracking plan implemented April-May 2022 - TODO - Remove this line
    const errorMessage = `Dynamic Lead Form submission error, path ${document.location.href}`;
    trackError(error, errorMessage, document.documentElement.className);
    errorsContainer.show();
    throw error;
  }
}

const buildNotesContent = ({ notes: initialNotesContent = '', ...notesValues }) => {
  return Object.keys(notesValues).reduce((notesContent, fieldName) => {
    if (fieldName && notesValues[fieldName]) {
      return `${notesContent}${fieldName}: ${notesValues[fieldName]}\n`;
    }
    return notesContent;
  }, initialNotesContent);
};

async function buildLeadData({
  product_interests: productInterests,
  company_name: companyName,
  desks_interested_min: desksInterestedMin,
  company_size: companySize,
  contact_name: contactName,
  marketing_consent: marketingConsent,
  email,
  market,
  phone,
  notes,
  leadSource,
  leadSourceSubtype,
  leadSourceDetail,
  ...additionalFields
} = {}) {
  const notesFieldData = buildNotesContent({ notes, ...additionalFields });
  const leadData = {
    submission_type: 'lead',
    lead_source: leadSource,
    lead_source_subtype: leadSourceSubtype,
    google_analytics: null,
    ...(companyName && { company_name: companyName }),
    ...(productInterests && { product_interests: [productInterests] }),
    ...(desksInterestedMin && { desks_interested_min: desksInterestedMin }),
    ...(companySize && { company_size: companySize }),
    ...(contactName && { contact_name: contactName }),
    ...(phone && { phone }),
    ...(notesFieldData && { notes: notesFieldData }),
    marketing_consent: marketingConsent
  };

  leadData.google_analytics = await getGoogleAnalyticsData();
  delete leadData.google_analytics.gauserid; // removed by request of Billy Lam

  if (email) {
    leadData.email = email;
    leadData.email_sha256 = await sha256(email);
  }

  if (market) {
    leadData.city = market;
    leadData.interests = { market_ids: [market] };
  }

  return leadData;
}

function trackFormSubmission(leadFormData) {
  const copyLeadFormData = { ...leadFormData };
  delete copyLeadFormData.leadSource;
  delete copyLeadFormData.leadSourceSubtype;
  delete copyLeadFormData.leadSourceDetail;
  return sendLeadFormHeapEvents(copyLeadFormData, leadFormData.leadSourceDetail, leadFormData.leadSourceSubtype);
}

function showThankYou() {
  hideElements('#js-dynamic-form-container');
  showElements('#js-dynamic-form-lead-success');
}
